/**
 * MacroStepParser - Parses DSL macro step strings into typed commands.
 *
 * Extracted from MacroManager to keep both files under size caps.
 * Supports grammar v1 (key=, string=, gpad=) and grammar v2 (mouse=, touch=).
 *
 * Grammar v2 additions:
 * - mouse=move^dx,dy@timing    → relative mouse move (delta)
 * - mouse=down^button@timing   → mouse button down
 * - mouse=up^button@timing     → mouse button up
 * - mouse=wheel^deltaY@timing  → mouse wheel scroll
 * - touch=down@timing^x,y      → touch start at normalized 0–1 coords
 * - touch=move@timing^x,y      → touch move to normalized 0–1 coords
 * - touch=up@timing^x,y        → touch end at normalized 0–1 coords
 */

export type MacroStepType = 'key' | 'string' | 'gpad' | 'mouse' | 'touch';

export interface ParsedMacroStep {
  type: MacroStepType;
  value: string;
  timing: number;
  hold: number;
}

export interface ParsedMouseStep {
  type: 'mouse';
  action: 'move' | 'down' | 'up' | 'wheel';
  timing: number;
  params: { dx?: number; dy?: number; button?: number; deltaY?: number };
}

export interface ParsedTouchStep {
  type: 'touch';
  action: 'down' | 'move' | 'up';
  timing: number;
  x: number;
  y: number;
}

export type ParsedStep = ParsedMacroStep | ParsedMouseStep | ParsedTouchStep;

const DEFAULT_TIMING_MS = 100;
const DEFAULT_HOLD_MS_KEYBOARD = 20;
const DEFAULT_HOLD_MS_GAMEPAD = 150;

/**
 * Parse modifier suffixes from a macro value string.
 * - `@<number>` = timing (delay before action), default 100ms
 * - `^<number>` = hold (key/button hold duration)
 */
function parseMacroString(
  raw: string,
  defaultHold: number = DEFAULT_HOLD_MS_KEYBOARD,
): { value: string; timing: number; hold: number } {
  const at = raw.indexOf('@');
  const caret = raw.indexOf('^');

  let endIdx = raw.length;
  if (at !== -1 && caret !== -1) endIdx = Math.min(at, caret);
  else if (at !== -1) endIdx = at;
  else if (caret !== -1) endIdx = caret;

  const value = raw.slice(0, endIdx);

  const timingMatch = raw.match(/@(\d+)/);
  const holdMatch = raw.match(/\^(\d+)/);

  const timing = timingMatch ? Number(timingMatch[1]) : DEFAULT_TIMING_MS;
  const hold = holdMatch ? Number(holdMatch[1]) : defaultHold;

  return { value, timing, hold };
}

/**
 * Parse mouse= grammar v2 production.
 * Format: mouse=<action>^<params>@<timing>
 *   - mouse=move^dx,dy@timing
 *   - mouse=down^button@timing
 *   - mouse=up^button@timing
 *   - mouse=wheel^deltaY@timing
 */
function parseMouseStep(raw: string): ParsedMouseStep | null {
  const timingMatch = raw.match(/@(\d+)/);
  const timing = timingMatch ? Number(timingMatch[1]) : DEFAULT_TIMING_MS;

  const caretIdx = raw.indexOf('^');
  const atIdx = raw.indexOf('@');
  const actionStr = caretIdx !== -1 ? raw.slice(0, caretIdx) : (atIdx !== -1 ? raw.slice(0, atIdx) : raw);

  const paramEnd = atIdx !== -1 && atIdx > (caretIdx !== -1 ? caretIdx : 0) ? atIdx : raw.length;
  const paramStr = caretIdx !== -1 ? raw.slice(caretIdx + 1, paramEnd) : '';

  switch (actionStr) {
    case 'move': {
      const parts = paramStr.split(',');
      if (parts.length !== 2) return null;
      const dx = Number(parts[0]);
      const dy = Number(parts[1]);
      if (Number.isNaN(dx) || Number.isNaN(dy)) return null;
      return { type: 'mouse', action: 'move', timing, params: { dx, dy } };
    }
    case 'down': {
      const button = paramStr ? Number(paramStr) : 0;
      if (Number.isNaN(button)) return null;
      return { type: 'mouse', action: 'down', timing, params: { button } };
    }
    case 'up': {
      const button = paramStr ? Number(paramStr) : 0;
      if (Number.isNaN(button)) return null;
      return { type: 'mouse', action: 'up', timing, params: { button } };
    }
    case 'wheel': {
      const deltaY = paramStr ? Number(paramStr) : 120;
      if (Number.isNaN(deltaY)) return null;
      return { type: 'mouse', action: 'wheel', timing, params: { deltaY } };
    }
    default:
      return null;
  }
}

/**
 * Parse touch= grammar v2 production.
 * Format: touch=<action>@<timing>^<x>,<y>
 *   - touch=down@timing^x,y
 *   - touch=move@timing^x,y
 *   - touch=up@timing^x,y
 *
 * x and y are 0–1 normalized coordinates (element-relative).
 */
function parseTouchStep(raw: string): ParsedTouchStep | null {
  const timingMatch = raw.match(/@(\d+)/);
  const timing = timingMatch ? Number(timingMatch[1]) : DEFAULT_TIMING_MS;

  const atIdx = raw.indexOf('@');
  const caretIdx = raw.indexOf('^');

  const actionStr = atIdx !== -1 ? raw.slice(0, atIdx) : (caretIdx !== -1 ? raw.slice(0, caretIdx) : raw);

  if (actionStr !== 'down' && actionStr !== 'move' && actionStr !== 'up') {
    return null;
  }

  const paramStr = caretIdx !== -1 ? raw.slice(caretIdx + 1) : '';
  const parts = paramStr.split(',');
  if (parts.length !== 2) return null;

  const x = Number(parts[0]);
  const y = Number(parts[1]);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;

  return { type: 'touch', action: actionStr, timing, x, y };
}

/**
 * Parse a single macro step string into a typed command.
 *
 * Returns null for malformed steps (logs a warning, never throws).
 */
export function parseMacroStep(step: string): ParsedStep | null {
  if (step.startsWith('key=')) {
    return { type: 'key', ...parseMacroString(step.substring(4)) };
  }

  if (step.startsWith('string=')) {
    return { type: 'string', ...parseMacroString(step.substring(7)) };
  }

  if (step.startsWith('gpad=')) {
    return { type: 'gpad', ...parseMacroString(step.substring(5), DEFAULT_HOLD_MS_GAMEPAD) };
  }

  if (step.startsWith('mouse=')) {
    const parsed = parseMouseStep(step.substring(6));
    if (!parsed) {
      console.warn('[MacroStepParser] Malformed mouse step — skipping:', step);
      return null;
    }
    return parsed;
  }

  if (step.startsWith('touch=')) {
    const parsed = parseTouchStep(step.substring(6));
    if (!parsed) {
      console.warn('[MacroStepParser] Malformed touch step — skipping:', step);
      return null;
    }
    return parsed;
  }

  console.warn('[MacroStepParser] Unknown macro step — skipping:', step);
  return null;
}

export class MacroStepParser {
  public parse(step: string): ParsedStep | null {
    return parseMacroStep(step);
  }

  public parseAll(steps: string[]): ParsedStep[] {
    const results: ParsedStep[] = [];
    for (const step of steps) {
      const parsed = parseMacroStep(step);
      if (parsed) results.push(parsed);
    }
    return results;
  }
}
